import React, { useRef, useEffect, useState } from 'react';
import { toJS } from 'mobx';
import { observer } from 'mobx-react-lite';
import { WidgetCard } from '../WidgetCard';
import { widgetHeight, widgetWidth } from './../../AdminLayout';
import { Dropdown, Popup, Ref } from 'semantic-ui-react';
import { BottomButtonRow, Message, CardBodyWrapper } from '../Styles';
import { ButtonWithConfirmation } from '../ButtonWithConfirmation';
import useStores from '../../../../hooks/use-store';
import { engineToolType } from '../../../../stores/reducers/engineTools';

const getStreamerOptions = (data) =>
  data?.map(({ streamerID, streamer }) => ({
    key: streamerID,
    text: streamer,
    value: streamerID,
  })) ?? [];

export const SwitchStreamerWidget = observer(() => {
  const {
    AuctionStore: {
      sortedActiveAuctions,
      getSortedActiveAuctions,
      loadAuctionLaneOptions,
      laneOptions,
    },
    EngineToolStore: {
      data,
      engineFetch: fetch,
      enginePost: post,
      widgetReloading,
    },
  } = useStores();
  const [streamerValue, setStreamerValue] = useState();
  const [streamers, setStreamers] = useState(data);
  const [message, setMessage] = useState(null);
  const [success, setSuccess] = useState(null);
  const laneRef = useRef();
  const streamerRef = useRef();
  const {
    onAuctionChanged,
    onLaneChanged,
    auction,
    lane,
    isLoadingAuctions,
    isLoadingLanes,
    auctions,
    lanes,
  } = useAuctionAndLanes({
    sortedActiveAuctions,
    getSortedActiveAuctions,
    loadAuctionLaneOptions,
    laneOptions,
    laneRef,
  });

  const statusMessageHandler = ({ success, message }) => {
    setMessage(message);
    setSuccess(success);
  };

  const handleConfirmation = (e) => {
    e.preventDefault();
    if (auction?.auctionId && !lane?.laneId) {
      post(engineToolType.switchAuctionStreamers, {
        auctionID: auction.auctionId,
        streamerID: streamerValue,
      }).then(statusMessageHandler);
      return;
    }

    post(engineToolType.switchLaneStreamers, {
      auctionID: auction?.auctionId,
      laneID: lane?.laneId,
      streamerID: streamerValue,
    }).then(statusMessageHandler);
  };

  useEffect(() => {
    fetch(engineToolType.getStreamers);
  }, [streamers]);

  const isLoading =
    widgetReloading[engineToolType.getStreamers] ||
    widgetReloading[engineToolType.switchAuctionStreamers] ||
    widgetReloading[engineToolType.switchLaneStreamers];

  const streamerListEnabled = auction?.auctionId || lane?.laneId;
  return (
    <WidgetCard
      heading="Audio Streamer"
      description="Choose from one of the following streamers below to switch the auction to. This function will call reset the engine cache."
      width="450px"
      height="300px"
    >
      <CardBodyWrapper>
        <Dropdown
          placeholder="Select Auction"
          clearable
          fluid
          selection
          options={auctions}
          onChange={(e, data) => {
            onAuctionChanged(data.value, e.currentTarget.textContent);
            statusMessageHandler({ success: null, message: null });
          }}
          disabled={isLoadingAuctions || isLoading}
        />
        <Ref innerRef={laneRef}>
          <Dropdown
            placeholder="Select Lane"
            clearable
            fluid
            selection
            options={lanes}
            onChange={(e, data) => {
              onLaneChanged(data.value, e.currentTarget.textContent.text);
              statusMessageHandler({ success: null, message: null });
              if (data?.value) {
                post(engineToolType.getStreamers, {
                  auctionID: auction.auctionId,
                  laneID: data.value,
                }).then(([{ streamerID }]) => setStreamerValue(streamerID));
                return;
              }
              const streamerClearButton =
                streamerRef.current.querySelector('.clear');
              if (streamerClearButton) streamerClearButton.click();
            }}
            disabled={isLoadingLanes || isLoading}
          />
        </Ref>
        <Ref innerRef={streamerRef}>
          <Dropdown
            placeholder="Select Streamer"
            clearable
            fluid
            selection
            options={getStreamerOptions(data)}
            onChange={(e, data) => {
              setStreamerValue(data.value);
              if (!data?.value)
                statusMessageHandler({ success: null, message: null });
            }}
            disabled={!streamerListEnabled}
            value={streamerValue}
          />
        </Ref>
      </CardBodyWrapper>
      <BottomButtonRow buttonWidth="190px">
        {message ? (
          <Popup
            data-testid="streamer-switch-popup"
            key="streamer-switch"
            content={message}
            header="Details"
            size="small"
            trigger={
              <Message success={success}>
                {success
                  ? '😃 Streamers were successfully switched!'
                  : '🤔 Oops, an error occurred while trying to switch streamers.'}
              </Message>
            }
          />
        ) : (
          <Message />
        )}

        <ButtonWithConfirmation
          buttonText="Change Streamer"
          buttonIcon="heartbeat"
          onConfirmation={handleConfirmation}
          disabled={!streamerValue}
          loading={isLoading}
        />
      </BottomButtonRow>
    </WidgetCard>
  );
});

export default SwitchStreamerWidget;

export const useAuctionAndLanes = ({
  sortedActiveAuctions = [],
  getSortedActiveAuctions = [],
  loadAuctionLaneOptions = [],
  laneOptions = [],
  laneRef = null,
  enableLaneLoading = true,
}) => {
  const [auction, setAuction] = useState();
  const [isLoadingAuctions, setIsLoadingAuctions] = useState(true);

  const [lane, setLane] = useState();
  const [isLoadingLanes, setIsLoadingLanes] = useState(false);

  const getOptionsWithoutAll = (data) =>
    [...data].filter((option) => option.value !== 'ALL');

  const lanesNoAll = getOptionsWithoutAll(laneOptions);
  const [lanes, setLanes] = useState(lanesNoAll);

  const setAuctionAndLoadLanes = (auctionId) => loadLaneOptions(auctionId);

  const clearSelectedLane = () => {
    if (laneRef?.current) {
      const $clearButton = laneRef.current.querySelector('.clear');
      if ($clearButton) $clearButton.click();
    }
  };

  const onAuctionChanged = (value = null, text = null) => {
    const auctionName = text;
    const auctionId = value;

    clearSelectedLane();

    if (!auctionId || !auctionName) {
      setAuction({});
      setLanes([]);
      return;
    }

    setAuction({ auctionId, auctionName });
    enableLaneLoading && setAuctionAndLoadLanes(auctionId);
  };

  const loadLaneOptions = (auctionId) => {
    setIsLoadingLanes(true);

    loadAuctionLaneOptions(auctionId).then((laneOptions) => {
      setLanes(getOptionsWithoutAll(laneOptions));
      setIsLoadingLanes(false);
    });
  };

  const onLaneChanged = (value = null, text = null) => {
    setLane({ laneId: value, laneName: text });
    if (!value || !text) return;
  };

  useEffect(() => {
    getSortedActiveAuctions();
    setIsLoadingAuctions(false);
  }, []);

  useEffect(() => {}, [auction]);

  return {
    onAuctionChanged,
    onLaneChanged,
    auction,
    lane,
    isLoadingAuctions,
    isLoadingLanes,
    auctions: toJS(sortedActiveAuctions),
    lanes: toJS(lanes),
  };
};
